import axios from 'axios';
import { keycloak } from '../auth';

// Tasker API client — pattern from api/ipezby/client.ts
// Separate axios instance with Keycloak token, calling Tasker API directly

const TASKER_API_URL = import.meta.env.VITE_TASKER_API_URL;

export const taskerAxios = axios.create({
  baseURL: TASKER_API_URL || '',
  timeout: 10_000,
});

// Request interceptor: refresh Keycloak token and add to header
taskerAxios.interceptors.request.use(
  async (config) => {
    if (keycloak.token) {
      try {
        await keycloak.updateToken(30);
      } catch {
        keycloak.login();
        return Promise.reject(new Error('Token refresh failed'));
      }
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: silent fail (no redirect — tasker auth is independent)
taskerAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect to Keycloak login on tasker 401 — would cause infinite loop
    // Tasker auth may not be ready yet, just fail silently
    return Promise.reject(error);
  },
);

// Minimal type — only fields we display
export interface TaskerTask {
  id: number;
  title: string;
  deadline: string | null;
  status: {
    key: string;
    default_display_name: string;
    color: string;
    is_terminal: boolean;
  } | null;
}

export const taskerApi = {
  /**
   * List tasks assigned to current user (Tasker determines user from Keycloak token)
   */
  /** Get current tasker user (to know our assignee_id) */
  getMe: async (): Promise<{ id: number } | null> => {
    if (!TASKER_API_URL) return null;
    try {
      const { data } = await taskerAxios.get('/users/me');
      return data;
    } catch {
      return null;
    }
  },

  listMyTasks: async (): Promise<TaskerTask[]> => {
    if (!TASKER_API_URL) return [];
    try {
      // First get our tasker user ID
      const me = await taskerApi.getMe();
      if (!me) return [];

      const { data } = await taskerAxios.get('/tasks', {
        params: {
          assignee_id: me.id,
          is_completed: false,
          page_size: 50,
          sort_by: 'deadline',
          sort_order: 'asc',
        },
      });
      // Tasker returns { items: [...], total: N }
      return data.items || data.data || data || [];
    } catch (err) {
      console.warn('Tasker API error:', err);
      return [];
    }
  },
};
